import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Box,
  Stack,
  Avatar,
  CircularProgress,
  Typography,
  Grid,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DescriptionIcon from '@mui/icons-material/Description';
import BackupOutlinedIcon from '@mui/icons-material/BackupOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { postFeed, updateFeed, getFeeds, getMyFeeds } from '../../../../Redux/Reducers/communityReducer';
import { useQuantityContext } from '../../../QuantityContext';
import { useNavigate } from 'react-router-dom';
import { useUserDetailContext } from '../../../UserDetailContext';


const MAX_MEDIA_FILES = 10;

export default function CreateFeedModal({ open, onClose, initialContent = '', initialMedia = [], onSubmit, postId = null, isEditMode = false }) {
  const [postContent, setPostContent] = useState(initialContent);
  const [files, setFiles] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { openSnackbar } = useQuantityContext();
  const [isDragging, setIsDragging] = useState(false);
  const profile = useSelector(state => state.ProfileArray.professionaProfile) || {};
  const { userDetails } = useUserDetailContext();
  const userName =userDetails?.firstName + " " + userDetails?.lastName || "User";
  const exploreType = useSelector((state) => state?.community?.exploreType);
   const Personaldata = useSelector(
    (state) => state?.ProfileArray?.personalProfile?.data
  );
  // console.log("isEditModeNNN", isEditMode, initialContent, initialMedia)



 useEffect(() => {
  const normalizeMediaType = (mimeType) => {
    if (mimeType.includes('image')) return 'image';
    if (mimeType.includes('video')) return 'video';
    if (mimeType.includes('pdf')) return 'pdf';
    return mimeType.split('/')[0];
  };

  if (isEditMode) {
    const mapped = initialMedia?.map((m) => ({
      ...m,
      preview: m.url,
      file: null,
      thumbnail: m.thumbnail || null,
      type: normalizeMediaType(m.type) // Normalize the type
    })) || [];
    setFiles(mapped);
  } else if (initialMedia?.length) {
    const mapped = initialMedia?.map((m) => ({
      ...m,
      file: null,
      type: normalizeMediaType(m.type) // Normalize the type
    })) || [];
    setFiles(mapped);
  }
}, [isEditMode, initialMedia]);

  const generateThumbnail = useCallback(file => new Promise(resolve => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    video.preload = 'metadata';
    video.src = URL.createObjectURL(file);
    video.onloadedmetadata = () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      video.currentTime = video.duration * 0.25;
    };
    video.onseeked = () => {
      canvas.getContext('2d').drawImage(video, 0, 0);
      URL.revokeObjectURL(video.src);
      resolve(canvas.toDataURL('image/jpeg'));
    };
    video.onerror = () => resolve(null);
  }), []);

  const handleUpload = async e => {
    let selected = Array.from(e.target.files);
    if (files?.length + selected?.length > MAX_MEDIA_FILES) {
      openSnackbar(`Maximum ${MAX_MEDIA_FILES} files allowed. Remove some first.`, 'error');
      return;
    }
    const processed = await Promise.all(
      selected?.map(async file => {
        const type = file.type.split('/')[0];
        let thumb = null;
        if (type === 'video') thumb = await generateThumbnail(file);
        return { file, type, preview: URL.createObjectURL(file), thumbnail: thumb };
      })
    );
    setFiles(prev => [...prev, ...processed]);
    e.target.value = '';
  }

  const handleRemove = idx => {
    const newFiles = [...files];
    // Only revoke object URLs for new files
    if (!newFiles[idx].file) {
      URL.revokeObjectURL(newFiles[idx].preview);
    }
    newFiles.splice(idx, 1);
    setFiles(newFiles);
    setCurrentIdx(newFiles.length ? Math.min(idx, newFiles.length - 1) : 0);
  };

  const handleSubmit = async () => {
    setLoading(true);
    const form = new FormData();
    form.append('content', postContent);
    form.append('is_visible', 'true');
    form.append('post_category', exploreType);
    
    // Handle both existing and new files
    for (let f of files) {
      if (f.file) {
        // New file - use directly
        form.append('media', f.file);
      } else if (f.url) {
        // Existing file - fetch from URL
        const response = await fetch(f.url);
        const blob = await response.blob();
        form.append('media', blob, f.filename || 'file');
      }
    }
    
    try {
      if (isEditMode) {
        await dispatch(updateFeed({ id: postId, data: form }));
        openSnackbar('Post updated successfully!', 'success');
      } else {
        await dispatch(postFeed(form));
        openSnackbar('Posted successfully!', 'success');
        navigate('/my-feeds?tab=second');
      }
      dispatch(getMyFeeds({ type: exploreType }));
      dispatch(getFeeds({ type: exploreType, my_posts: false }));
      onClose();
    } catch {
      openSnackbar('Error processing your request!', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDragOver = (e) => {
  e.preventDefault();
  setIsDragging(true);
};

const handleDragLeave = (e) => {
  e.preventDefault();
  setIsDragging(false);
};

const handleDrop = (e) => {
  e.preventDefault();
  setIsDragging(false);
  const droppedFiles = Array.from(e.dataTransfer.files);
  if (droppedFiles?.length) {
    handleUpload({ target: { files: droppedFiles } }); // mimic file input
  }
};



  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" sx={{ '& .MuiPaper-root': { borderRadius: '12px' } }}>
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        color: 'black',
        py: 1.5,
        px: 3
      }}>
        <Typography variant="h6" fontWeight="bold">
          {isEditMode ? 'Edit Post' : 'Create New Post'}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'black' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ py: 3, px: 3, p:"1.5rem", }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} >
            <Paper sx={{ 
              p: 0.5, 
              minHeight: 350, 
              display: 'flex', 
              flexDirection: 'column',
              borderRadius: '12px',
              boxShadow: '0px 4px 12px rgba(0,0,0,0.1)'
            }}>
              {files?.length > 0 ? (
                <>
                  <Box sx={{ 
                    mb: 2, 
                    flex: 1, 
                    position: 'relative',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    bgcolor: 'grey.100',
                    minHeight: 250
                  }}>
                    {files[currentIdx].type === 'image' && (
                      <Box 
                        component="img" 
                        src={files[currentIdx].preview} 
                        alt="" 
                        sx={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'contain',
                          maxHeight: 250
                        }} 
                      />
                    )}
                    {files[currentIdx].type === 'video' && (
                      <Box 
                        component="video" 
                        src={files[currentIdx].preview} 
                        controls 
                        sx={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'contain',
                          maxHeight: 250
                        }} 
                      />
                    )}
                    {files[currentIdx].type === 'pdf' && (
                      <Box 
                        component="embed" 
                        src={files[currentIdx].preview} 
                        type="application/pdf" 
                        sx={{ 
                          width: '100%', 
                          height: '100%',
                          minHeight: 250
                        }} 
                      />
                    )}
                  </Box>
                  
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(5, 64px)',
                      gap: 1, // spacing between thumbnails
                      py: 1,
                      px: 0.5
                    }}
                  >
                    {files?.map((f, i) => (
                      <Box
                        key={i}
                        sx={{
                          position: 'relative',
                          width: 64,
                          height: 64,
                          borderRadius: '4px',
                          overflow: 'hidden',
                          border: i === currentIdx ? '2px solid' : '1px solid',
                          borderColor: i === currentIdx ? 'primary.main' : 'divider',
                          cursor: 'pointer',
                          transition: 'border-color 0.2s'
                        }}
                        onClick={() => setCurrentIdx(i)}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleRemove(i); }}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            zIndex: 1,
                            bgcolor: 'rgba(0,0,0,0.6)',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                          }}
                        >
                          <CloseIcon fontSize="small" sx={{ color: '#fff' }} />
                        </IconButton>

                        {f.type === 'image' && (
                          <Box
                            component="img"
                            src={f.preview}
                            alt=""
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        )}

                        {f.type === 'video' && (
                          <Box sx={{
                            width: '100%',
                            height: '100%',
                            position: 'relative',
                            bgcolor: 'grey.900'
                          }}>
                            <Box
                              component="img"
                              src={f.thumbnail || ''}
                              alt=""
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                opacity: f.thumbnail ? 1 : 0.5
                              }}
                            />
                            <PlayArrowIcon sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              color: '#fff',
                              fontSize: 24
                            }} />
                          </Box>
                        )}

                        {f.type === 'pdf' && (
                          <Box sx={{
                            width: '100%',
                            height: '100%',
                            bgcolor: 'grey.200',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <DescriptionIcon sx={{ color: 'error.main', fontSize: 28 }} />
                          </Box>
                        )}
                      </Box>
                    ))}

                    {files?.length < MAX_MEDIA_FILES && (
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: '4px',
                          border: '1px dashed',
                          borderColor: 'primary.main',
                          bgcolor: 'grey.50',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                          '&:hover': { bgcolor: 'primary.light' }
                        }}
                        onClick={() => fileRef.current.click()}
                      >
                        <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                      </Box>
                    )}
                  </Box>

                </>
              ) : (
                <Box 
                  sx={{ 
                    flex: 1, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    flexDirection: 'column', 
                    cursor: 'pointer',
                    border: '2px dashed',
                   borderColor: isDragging ? 'primary.main' : 'divider',
                    borderRadius: '8px',
                    background: isDragging ? 'linear-gradient(90deg, rgba(2,0,36,0.1) 0%, rgba(0,0,105,0.1) 0%, rgba(0,212,255,0.1) 100%)' : 'transparent',
                    py: 6,
                    transition: 'border-color 0.2s',
                    '&:hover': { 
                      // borderColor: 'primary.main',
                      background: "linear-gradient(90deg, rgba(2,0,36,0.1) 0%, rgba(0,0,105,0.1) 0%, rgba(0,212,255,0.1) 100%)"
                    
                    }
                  }} 
                  onClick={() => fileRef.current.click()}
                   onDragOver={handleDragOver}
                   onDragLeave={handleDragLeave}
                   onDrop={handleDrop}
                >
                  <BackupOutlinedIcon sx={{ fontSize: 64, color: 'grey', mb: 1,  }} />
                  <Typography variant="body1" color="text.secondary" textAlign="center">
                    Click to upload or drag & drop<br/>
                    <Typography component="span" variant="caption" color="text.secondary">
                      (Max {MAX_MEDIA_FILES} files)
                    </Typography>
                  </Typography>
                </Box>
              )}
              <input 
                ref={fileRef} 
                type="file" 
                hidden 
                multiple 
                accept="image/*,video/*,application/pdf" 
                onChange={handleUpload} 
              />
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Stack spacing={2} sx={{ height: 1 }}>
              <Stack 
                direction="row" 
                spacing={2} 
                alignItems="center" 
                sx={{ 
                  p: 1, 
                  // bgcolor: 'grey.50', 
                  borderRadius: '8px',
                  // border: '1px solid',
                  // borderColor: 'divider'
                }}
              >
                <Avatar 
                  src={ exploreType == "PERSONAL" ?  Personaldata?.user_profile_url : profile?.user_profile_url} 
                  sx={{ width: 48, height: 48 }} 
                />
               <Box sx={{display:"flex", flexDirection:"column"}}>
                 <Typography fontWeight="bold" fontSize="1.1rem">
                 {userName}
                </Typography>
                 {exploreType == "PERSONAL" ? "":<Typography fontWeight="normal" fontSize="smaller">
                 {profile?.designation || ''}
                </Typography>}
               </Box>
              </Stack>
              
              <TextField
                multiline
                placeholder="Share your thoughts, ideas, or updates..."
                value={postContent}
                onChange={e => setPostContent(e.target.value)}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '&:hover fieldset': { borderColor: 'primary.main' },
                    height: '270px',
                    alignItems: 'flex-start',
                  },
                  '& .MuiInputBase-input': {
                    height: '100% !important',
                    overflowY: 'auto !important',
                    resize: 'none',
                    padding: '5px 14px',
                    wordWrap: 'break-word',
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'break-word',
                  }
                }}
                inputProps={{ maxLength: 1000 }}
              />
              <Typography 
                variant="caption" 
                color="text.secondary" 
                textAlign="right"
                sx={{ mt: -1 }}
              >
                {postContent?.length}/1000 characters
              </Typography>

                    <DialogActions sx={{  pb: 0.5 }}>
        {/* <Button 
          onClick={onClose} 
          variant="outlined"
          sx={{ 
            borderRadius: '20px',
            px: 3,
            textTransform: 'none',
            fontWeight: 'bold'
          }}
        >
          Cancel
        </Button> */}
        <Button 
          variant="contained" 
          disabled={(!postContent.trim() && files?.length === 0) || loading}
          onClick={handleSubmit} 
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{ 
            px: 3,
            textTransform: 'none',
            fontWeight: 'bold',
            boxShadow: 'none',
            '&:hover': { boxShadow: '0px 4px 8px rgba(0,0,0,0.2)' }
          }}
        >
          {isEditMode ? 'Update Post' : 'Post'}
        </Button>
      </DialogActions>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}